/* eslint-disable @typescript-eslint/no-explicit-any */
import IndexedList from '../shared/IndexedList';
import normalizePriceTiers from '../shared/normalizePriceTiers';
import { mapObject } from '../helpers/mapObject';

import { Market } from '../../types/shared/Market';
import { DEFAULT_FORMS } from '../shared/constants/Constants';
import SignupFormModel from '../../components/FormBuilder/SignupFormModel';
import FormModel from '../../components/FormBuilder/FormModel';

import {
  Account,
  AccountWithSummary,
  VendorProfileTemplate,
  MapSettings,
  PaymentSettings,
  ProfileSettings,
} from '../../types/shared/Account';
import { User } from '../../types/shared/User';
import { Addon } from '../../types/shared/Addon';
import { VendorAccount } from '../../types/shared/VendorAccount';
import { Season } from '../../types/shared/Season';
import { PlacementByVendor } from '../../types/shared/Placement';
import { PositionMapByMarket } from '../../types/shared/PositionMap';
import { Tier } from '../../types/shared/Tier';
import { PaymentsAndSalesByMarket } from '../../types/shared/MarketSales';
import {
  Vendor,
  VendorSummary,
  VendorSummaryWithMarkets,
  VendorSummaryWithMarketsAndFields,
  Profile,
} from '../../types/shared/Vendor';
import { Tag } from '../../types/shared/Tag';
import { AttendanceByMarket } from '../../types/shared/Attendance';
import { VendorPortalPage } from '../../types/shared/VendorPortalPage';

// TODO: Move this type
import { Location } from '../../components/Embed/LocationsEmbed';

type FetchRemote<T> = {
  endpoint: string;
  responseTransform?: (data: any) => T;
};

type SaveRemote<T> = {
  endpoint: string;
  requestTransform?: (value: T, previousValue: T) => ObjectOf<any>;
  responseTransform?: (data: any) => any;
  afterSave?: (resolvedKey: string, data: ObjectOf<any>) => void;
};

type ResourceDefinition<T> = {
  fetch?: FetchRemote<T>;
  save?: SaveRemote<T>;
};

// TODO: Move this list elsewhere?
// See frontend/webpack.common.js for full list of entrypoints
type EntryPoint =
  // This is the main web app, aka the market manager interface
  | 'app'
  // This is any embedded feature; 'locations', 'map', 'preview', 'profiles', 'signup'
  | 'embed'
  // This is any standalone entrypoint for vendors; 'vendor-invoice', 'vendor-portal', 'vendor-profile'
  | 'vendor';

const resources = mapObject({
  isUpdateAvailable: defineResource<boolean>(),
  entryPoint: defineResource<EntryPoint>(),
  accountID: defineResource<string>(),
  account: defineResource<Account>(),
  user: defineResource<User>(),
  vendorAccount: defineResource<VendorAccount>(),
  season: defineResource<Season>(),
  accounts: defineResource<IndexedList<AccountWithSummary>>({
    fetch: {
      endpoint: '/api/accounts',
      responseTransform: (data: { accounts: Array<AccountWithSummary> }) => {
        return new IndexedList(data.accounts);
      },
    },
  }),
  users: defineResource<Map<string, User>>({
    fetch: {
      endpoint: '/api/users',
      responseTransform: (data: { users: Array<User> }) => {
        return new Map(data.users.map((user) => [user?.id, user]));
      },
    },
  }),
  'forms/:id': defineResource<FormModel | SignupFormModel>({
    fetch: {
      endpoint: '/api/forms/:id',
      responseTransform: (data) => {
        if (data?.id === DEFAULT_FORMS.signup.id) {
          return new SignupFormModel(data);
        }
        return new FormModel(data);
      },
    },
    save: {
      endpoint: '/api/forms/:id',
    },
  }),
  vendorAccounts: defineResource<Map<string, VendorAccount>>({
    fetch: {
      endpoint: '/api/vendor-accounts',
      responseTransform: (data: { vendorAccounts: Array<VendorAccount> }) => {
        return new Map(
          data.vendorAccounts.map((vendorAccount) => [
            vendorAccount?.id,
            vendorAccount,
          ]),
        );
      },
    },
  }),
  markets: defineResource<IndexedList<Market>>({
    fetch: {
      endpoint: '/api/markets',
      responseTransform: (data) => new IndexedList(data.markets),
    },
  }),
  'markets/:id': defineResource<Market>({
    fetch: {
      endpoint: '/api/markets/:id',
      responseTransform: (data) => data.market,
    },
  }),
  addons: defineResource<Map<string, Addon>>({
    fetch: {
      endpoint: '/api/addons',
      responseTransform: (data: { addons: Array<Addon> }) => {
        return new Map(data.addons.map((addon) => [addon.id, addon]));
      },
    },
    save: {
      endpoint: '/api/addons',
      requestTransform: (addons) => ({
        addons: Array.from(addons.values()),
      }),
    },
  }),
  placement: defineResource<PlacementByVendor>({
    fetch: {
      endpoint: '/api/placement',
      responseTransform: (data) => data.placement,
    },
    save: {
      endpoint: '/api/placement',
      requestTransform: (newPlacement) => {
        return { newPlacement };
      },
    },
  }),
  position: defineResource<PositionMapByMarket>({
    fetch: {
      endpoint: '/api/position',
      responseTransform: (data) => data.positionMap,
    },
    save: {
      endpoint: '/api/position',
      requestTransform: (positionMap) => ({ positionMap }),
    },
  }),
  priceTiers: defineResource<IndexedList<Tier>>({
    fetch: {
      endpoint: '/api/pricing',
      responseTransform: (data) =>
        new IndexedList(normalizePriceTiers(data.tiers)),
    },
    save: {
      endpoint: '/api/pricing',
      requestTransform: (tiers) => ({ tiers }),
    },
  }),
  sales: defineResource<PaymentsAndSalesByMarket>({
    fetch: {
      endpoint: '/api/redemptions',
      responseTransform: ({ allMarketSales }) => allMarketSales,
    },
    save: {
      endpoint: '/api/redemptions',
      requestTransform: (newSales, fetchedSales) => {
        return { newSales, fetchedSales };
      },
    },
  }),
  vendors: defineResource<IndexedList<VendorSummary>>({
    fetch: {
      endpoint: '/api/vendors',
      responseTransform: (data) => new IndexedList(data.vendors),
    },
    save: {
      endpoint: '/api/vendors/:id',
    },
  }),
  'vendors/withMarket': defineResource<IndexedList<VendorSummaryWithMarkets>>({
    fetch: {
      endpoint: '/api/vendorsWithMarket',
      responseTransform: (data) => new IndexedList(data.vendors),
    },
    save: {
      endpoint: '/api/vendors/:id',
    },
  }),
  'vendors/:id/with-fields': defineResource<VendorSummaryWithMarketsAndFields>({
    fetch: {
      endpoint: '/api/vendors/:id/withFields',
      responseTransform: ({ vendor }) => {
        let { fields, ...other } = vendor;
        return { ...other, fields: new IndexedList(fields) };
      },
    },
    save: {
      endpoint: '/api/vendors/:id',
    },
  }),
  'vendors/:id': defineResource<Vendor>({
    fetch: {
      endpoint: '/api/vendors/:id',
      responseTransform: ({ vendor }) => {
        let { fields, ...other } = vendor;
        return { ...other, fields: new IndexedList(fields) };
      },
    },
    save: {
      endpoint: '/api/vendors/:id',
    },
  }),
  // Note: Was `vendors/:id`
  'profiles/:id': defineResource<Profile>({
    fetch: {
      endpoint: '/api/profiles/:id',
      responseTransform: ({ profile }) => {
        return { ...profile, fields: new IndexedList(profile.fields) };
      },
    },
    save: {
      endpoint: '/api/profiles/:id',
    },
  }),
  // TODO: Rename to activeVendors
  'vendors/active': defineResource<IndexedList<VendorSummaryWithMarkets>>({
    fetch: {
      endpoint: '/api/vendors/active',
      responseTransform: (data) => new IndexedList(data.vendors),
    },
  }),
  'vendors/all': defineResource<IndexedList<VendorSummaryWithMarkets>>({
    fetch: {
      endpoint: '/api/vendors/all',
      responseTransform: (data) => new IndexedList(data.vendors),
    },
  }),
  'vendors/active-with-fields': defineResource<
    IndexedList<VendorSummaryWithMarketsAndFields>
  >({
    fetch: {
      endpoint: '/api/vendors/activeWithFields',
      responseTransform: (data) => new IndexedList(data.vendors),
    },
  }),
  'vendors/with-fields': defineResource<
    IndexedList<VendorSummaryWithMarketsAndFields>
  >({
    fetch: {
      endpoint: '/api/vendors/withFields',
      // TODO: Remove this transform; we're literally just turning this back to an array in the application code.
      responseTransform: (data) => new IndexedList(data.vendors),
    },
  }),
  vendorLocations: defineResource<{ vendorLocations: Array<Location> }>({
    // TODO: Use responseTransform here to unwrap this object.
    fetch: {
      endpoint: '/api/vendor-locations',
    },
  }),
  tags: defineResource<Array<Tag>>({
    fetch: {
      endpoint: '/api/tags',
      responseTransform: ({ tags }) => tags,
    },
  }),
  attendance: defineResource<AttendanceByMarket>({
    fetch: {
      endpoint: '/api/attendance',
      responseTransform: ({ attendance }) => attendance,
    },
    save: {
      endpoint: '/api/attendance',
      requestTransform: (newAttendance, fetchedAttendance) => {
        return { newAttendance, fetchedAttendance };
      },
    },
  }),
  marketPages: defineResource<Array<VendorPortalPage>>({
    fetch: {
      endpoint: '/api/vendor-portal/pages',
      responseTransform: ({ pages }) => pages,
    },
  }),
  'vendorMarketPages/:market_id/:visibility': defineResource<
    Array<VendorPortalPage>
  >({
    fetch: {
      endpoint: '/api/vendor-portal-pages/:market_id/:visibility',
      responseTransform: ({ pages }) => pages,
    },
  }),
  vendorInfo: defineResource<VendorSummaryWithMarketsAndFields>({
    fetch: {
      endpoint: '/api/vendor-info',
      responseTransform: ({ vendor }) => {
        let { fields, ...other } = vendor;
        return { ...other, fields: new IndexedList(fields) };
      },
    },
  }),
  marketPagesAccount: defineResource<Account>({
    fetch: {
      endpoint: '/api/market-pages-account',
      responseTransform: ({ account }) => {
        return account;
      },
    },
  }),
  vendorMarkets: defineResource<IndexedList<Market>>({
    fetch: {
      endpoint: '/api/vendor-markets',
      responseTransform: ({ markets }) => new IndexedList(markets),
    },
  }),
  'accounts/:id/publicVendorProfileSettings': defineResource<{
    vendorProfileTemplate: VendorProfileTemplate;
    profiles: ProfileSettings;
  }>({
    fetch: {
      endpoint: '/api/accounts/:id/publicVendorProfileSettings',
    },
  }),
  'accounts/:id/publicMapSettings': defineResource<MapSettings>({
    fetch: {
      endpoint: '/api/accounts/:id/publicMapSettings',
    },
  }),
  paymentSettings: defineResource<PaymentSettings>({
    fetch: {
      endpoint: '/api/settings/payment',
    },
  }),
});

type ResourceList = typeof resources;
export type ResourceKey = keyof ResourceList;

export type ResourceTypeMap = {
  [K in ResourceKey]: ResourceList[K] extends ResourceDefinition<infer T>
    ? T
    : never;
};

export type ResourceTypeForKey<K extends ResourceKey> = ResourceTypeMap[K];

function defineResource<T>(definition: ResourceDefinition<T> = {}) {
  return (_key: string) => definition;
}

export function getResource<K extends ResourceKey>(key: K): ResourceList[K] {
  return resources[key];
}
